/**
 * Business Category Form Dialog
 *
 * Dialog for creating/editing business categories
 * Includes tabs for basic info, required documents, and license activities
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Icon,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import WDButton from "../../../../MaterialComponents/WDButton";
import AppService from "../../../../Configurations/Appservice";

// Available icons for categories
const availableIcons = [
  { value: "store", label: "Store" },
  { value: "restaurant", label: "Restaurant" },
  { value: "local_hospital", label: "Hospital" },
  { value: "apartment", label: "Apartment" },
  { value: "construction", label: "Construction" },
  { value: "school", label: "School" },
  { value: "business_center", label: "Business" },
  { value: "computer", label: "Computer" },
  { value: "shopping_cart", label: "Shopping" },
  { value: "factory", label: "Factory" },
  { value: "local_shipping", label: "Shipping" },
  { value: "flight", label: "Flight" },
];

// Color options
const colorOptions = [
  "#607D8B", "#FF5722", "#E91E63", "#9C27B0", "#795548", "#3F51B5",
  "#00BCD4", "#2196F3", "#4CAF50", "#FF9800", "#673AB7", "#009688",
];

const BusinessCategoryFormDialog = ({ open, category, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    nameAr: "",
    description: "",
    icon: "store",
    colour: "#607D8B",
    isActive: true,
    documents: [],
    licenseActivityCodes: [],
  });
  const [newActivityCode, setNewActivityCode] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);

  useEffect(() => {
    if (open) {
      // Load document types from API
      AppService.get("api/organization-document-types")
        .then((response) => {
          setDocumentTypes(response || []);
        })
        .catch((error) => {
          console.error("Failed to load document types", error);
        });
    }
  }, [open]);

  useEffect(() => {
    if (category) {
      setFormData({
        code: category.code || "",
        name: category.name || "",
        nameAr: category.nameAr || "",
        description: category.description || "",
        icon: category.icon || "store",
        colour: category.colour || "#607D8B",
        isActive: category.isActive !== false,
        documents: (category.documents || []).map(d => ({
          documentTypeId: d.documentTypeId,
          documentTypeCode: d.documentTypeCode,
          documentTypeName: d.documentTypeName,
          isMandatory: d.isMandatory,
        })),
        licenseActivityCodes: category.licenseActivityCodes || [],
      });
    } else {
      setFormData({
        code: "",
        name: "",
        nameAr: "",
        description: "",
        icon: "store",
        colour: "#607D8B",
        isActive: true,
        documents: [],
        licenseActivityCodes: [],
      });
    }
    setActiveTab(0);
  }, [category, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddDocument = (docTypeId) => {
    const docType = documentTypes.find((dt) => dt.id === docTypeId);
    if (docType && !formData.documents.find((d) => d.documentTypeId === docTypeId)) {
      handleChange("documents", [
        ...formData.documents,
        { 
          documentTypeId: docTypeId, 
          documentTypeCode: docType.code,
          documentTypeName: docType.name,
          isMandatory: true 
        },
      ]);
    }
  };

  const handleRemoveDocument = (docTypeId) => {
    handleChange(
      "documents",
      formData.documents.filter((d) => d.documentTypeId !== docTypeId)
    );
  };

  const handleToggleDocumentMandatory = (docTypeId) => {
    handleChange(
      "documents",
      formData.documents.map((d) =>
        d.documentTypeId === docTypeId ? { ...d, isMandatory: !d.isMandatory } : d
      )
    );
  };

  const handleAddActivityCode = () => {
    if (newActivityCode && !formData.licenseActivityCodes.includes(newActivityCode)) {
      handleChange("licenseActivityCodes", [
        ...formData.licenseActivityCodes,
        newActivityCode,
      ]);
      setNewActivityCode("");
    }
  };

  const handleRemoveActivityCode = (code) => {
    handleChange(
      "licenseActivityCodes",
      formData.licenseActivityCodes.filter((c) => c !== code)
    );
  };

  const handleSubmit = () => {
    // Format data for API request
    const requestData = {
      code: formData.code,
      name: formData.name,
      nameAr: formData.nameAr,
      description: formData.description,
      colour: formData.colour,
      icon: formData.icon,
      licenseActivityCodes: formData.licenseActivityCodes,
      documents: formData.documents.map(d => ({
        documentTypeId: d.documentTypeId,
        isMandatory: d.isMandatory,
      })),
      isActive: formData.isActive,
    };
    onSave(requestData);
  };

  const isEdit = Boolean(category);

  // Get available document types that aren't already added
  const availableDocTypes = documentTypes.filter(
    (dt) => !formData.documents.find((rd) => rd.documentTypeId === dt.id)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? `Edit Business Category: ${category.name}` : "Create Business Category"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Details" />
            <Tab label="Required Documents" />
            <Tab label="License Activities" />
          </Tabs>
        </Box>

        {/* Details Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code *"
                value={formData.code}
                onChange={(e) =>
                  handleChange("code", e.target.value.toUpperCase().replace(/[^A-Z_]/g, ""))
                }
                disabled={isEdit}
                helperText="Unique code (uppercase, underscores only)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name (English) *"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name (Arabic)"
                value={formData.nameAr}
                onChange={(e) => handleChange("nameAr", e.target.value)}
                dir="rtl"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={formData.icon}
                  label="Icon"
                  onChange={(e) => handleChange("icon", e.target.value)}
                >
                  {availableIcons.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Icon sx={{ color: formData.colour }}>{icon.value}</Icon>
                        <span>{icon.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <WDTypography variant="caption" color="text.secondary" gutterBottom>
                Color
              </WDTypography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {colorOptions.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleChange("colour", color)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: color,
                      cursor: "pointer",
                      border: formData.colour === color ? "3px solid #000" : "1px solid #ddd",
                    }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: `${formData.colour}20`,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Icon sx={{ color: formData.colour, fontSize: 40 }}>{formData.icon}</Icon>
                <Box>
                  <WDTypography variant="body1" fontWeight="medium">
                    {formData.name || "Category Name"}
                  </WDTypography>
                  <WDTypography variant="caption" color="text.secondary">
                    {formData.description || "Category description"}
                  </WDTypography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Required Documents Tab */}
        {activeTab === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Documents added here will be required for organizations in this business category,
              in addition to standard required documents.
            </Alert>

            {/* Add Document */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Add Document Type</InputLabel>
                <Select
                  label="Add Document Type"
                  value=""
                  onChange={(e) => handleAddDocument(e.target.value)}
                >
                  {availableDocTypes.map((dt) => (
                    <MenuItem key={dt.id} value={dt.id}>
                      {dt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Documents Table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Document Type</TableCell>
                    <TableCell align="center">Mandatory</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.documents.map((doc) => {
                    return (
                      <TableRow key={doc.documentTypeId}>
                        <TableCell>
                          <WDTypography variant="body2">
                            {doc.documentTypeName || doc.documentTypeCode}
                          </WDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={doc.isMandatory}
                            onChange={() => handleToggleDocumentMandatory(doc.documentTypeId)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveDocument(doc.documentTypeId)}
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {formData.documents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <WDTypography variant="body2" color="text.secondary" py={2}>
                          No industry-specific documents added
                        </WDTypography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* License Activities Tab */}
        {activeTab === 2 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              License activity codes help automatically suggest this business category
              when organizations upload their Trade License.
            </Alert>

            {/* Add Activity Code */}
            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
              <TextField
                size="small"
                label="Activity Code (e.g., 56.10)"
                value={newActivityCode}
                onChange={(e) => setNewActivityCode(e.target.value)}
                sx={{ width: 200 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddActivityCode}
                disabled={!newActivityCode}
              >
                Add
              </Button>
            </Box>

            {/* Activity Codes */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {formData.licenseActivityCodes.map((code) => (
                <Chip
                  key={code}
                  label={code}
                  onDelete={() => handleRemoveActivityCode(code)}
                />
              ))}
              {formData.licenseActivityCodes.length === 0 && (
                <WDTypography variant="body2" color="text.secondary">
                  No license activity codes added
                </WDTypography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <WDButton
          variant="gradient"
          onClick={handleSubmit}
          disabled={!formData.code || !formData.name}
        >
          {isEdit ? "Save Changes" : "Create Category"}
        </WDButton>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessCategoryFormDialog;
